/**
 * B606 (d) — the storefront ticket page's pure half. The ticket email links
 * to `/t#<token>` (one ticket) or `/t#<tok1>,<tok2>,…` (its one button,
 * capped at TICKETS_PAGE_TOKEN_CAP); the fragment never reaches a server.
 * Each token is opened through the PUBLIC `ticket.open` door, one call each.
 */
import { buyerTicketStatus } from "./confirmation.js";
import { ApiError, ApiRequestError } from "./api.js";

/** Mirrors the domain's cap — the email's button names at most this many tickets. */
export const TICKETS_PAGE_TOKEN_CAP = 10;

interface Money {
  readonly amount: number;
  readonly currency: string;
}

/** `ticket.open`'s page — one ticket and its order's summary (never the buyer). */
export interface TicketPageAnswer {
  readonly seller?: string;
  readonly status: string;
  /** B617 leg 2 — `bill`: who is on, when the show has a lineup. */
  readonly ticket: { readonly label: string; readonly bill?: string; readonly when?: string };
  readonly order?: {
    readonly lines: readonly { readonly label: string; readonly quantity: number; readonly unitPrice: Money; readonly net: Money }[];
    readonly fees: readonly { readonly label: string; readonly amount: Money }[];
    readonly tax?: Money;
    readonly total?: Money;
    readonly confirmationCode?: string;
  };
}

/** One token's face: the answer once it opens, the QR drawn from the token itself. Absent page = it did not open. */
export interface TicketFace {
  readonly token: string;
  readonly page?: TicketPageAnswer;
  readonly qr?: string;
  /** B1017 — the wire failed for this token (not a refusal): a retry may open it. */
  readonly unreached?: true;
}

/**
 * B1017 — a refusal is an answer about the TOKEN (it will not open, however
 * often it is asked): the client's schema refusing it before the wire, or a
 * non-retryable 4xx. Everything else — unreachable, deadline, a 5xx, a
 * retryable refusal (429), a stale-bundle shape break, anything unknown — is
 * the wire's failure, which a retry can cure.
 */
export function isTicketRefusal(error: unknown): boolean {
  if (error instanceof ApiRequestError) return true;
  return error instanceof ApiError && !error.retryable && error.status >= 400 && error.status < 500;
}

/**
 * B1017 — open each token ON ITS OWN, so one that throws never sinks the
 * tickets that did open (B1014's class: a refusal drawn as an outage). A
 * token naming no tenant is never sent; a refusal is a ticket that didn't
 * open; a wire failure marks the face `unreached`. Never rejects.
 */
export async function openTicketFaces(
  tokens: readonly string[],
  open: (tenantId: string, token: string) => Promise<{ readonly page?: TicketPageAnswer }>,
  drawQr: (token: string) => Promise<string>,
): Promise<readonly TicketFace[]> {
  return Promise.all(
    tokens.map(async (token): Promise<TicketFace> => {
      const tenantId = tenantOfTicketToken(token);
      if (tenantId === undefined) return { token };
      try {
        const body = await open(tenantId, token);
        if (body.page === undefined) return { token };
        return { token, page: body.page, qr: await drawQr(token) };
      } catch (error) {
        return isTicketRefusal(error) ? { token } : { token, unreached: true };
      }
    }),
  );
}

/** B1017 — the outage face is for a link where nothing opened and the wire failed for something. */
export function ticketsUnreached(faces: readonly TicketFace[]): boolean {
  return faces.some((f) => f.unreached === true) && !faces.some((f) => f.page !== undefined);
}

/** `/t#a,b` (or `/t/#…`) → the tokens, in order, once each, at most 10; anything else is not a ticket link. */
export function ticketTokensFrom(location: { readonly pathname: string; readonly hash: string }): readonly string[] | undefined {
  if (location.pathname !== "/t" && location.pathname !== "/t/") return undefined;
  const tokens = [...new Set(location.hash.replace(/^#/, "").split(",").map((s) => s.trim()).filter((s) => s.length > 0))];
  return tokens.length === 0 ? undefined : tokens.slice(0, TICKETS_PAGE_TOKEN_CAP);
}

/** The tenant a ttk1 token NAMES — read to claim it, never trusted (the server verifies). */
export function tenantOfTicketToken(token: string): string | undefined {
  const parts = token.split(".");
  if (parts.length !== 4 || parts[0] !== "ttk1") return undefined;
  try {
    const b64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4)), (c) => c.charCodeAt(0)))) as unknown;
    const t = typeof json === "object" && json !== null ? (json as Record<string, unknown>)["tenantId"] : undefined;
    return typeof t === "string" && t.length > 0 ? t : undefined;
  } catch {
    return undefined;
  }
}

/** The wallet's own words for a ticket's state (B448's table). */
export function ticketStatusWord(state: string): string {
  return buyerTicketStatus(state) ?? "Valid";
}

/** A ticket that can be scanned: shown with its QR. Canceled / used / expired ones are not offered to the gate. */
export function scannable(state: string): boolean {
  return state === "ISSUED" || state === "ACTIVE";
}

/** Tickets the order holds beyond those this link carries (the button stops at 10). */
export function moreTicketsInEmail(orderQuantity: number, shown: number): number {
  return Math.max(0, orderQuantity - shown);
}

/** The browser tab's words (the index ships "Checkout" — wrong on this page). */
export function ticketPageTitle(seller: string | undefined): string {
  return seller === undefined ? "Your tickets" : `Your tickets — ${seller}`;
}
